import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import { Trans, withTranslation, useTranslation } from 'react-i18next';
import CCEnablePlaceholderMatchingDialog from './CCEnablePlaceholderMatchingDialog';

const PREFIX = 'NewFlexItemList';
const classes = {
  root: `${PREFIX}-root`,
  textField: `${PREFIX}-textField`,
  inputField: `${PREFIX}-inputField`,
  toolbar: `${PREFIX}-toolbar`,
  toolbarTitle: `${PREFIX}-toolbarTitle`,
  createDialog: `${PREFIX}-createDialog`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.textField}`]: {
    margin: 0,
    padding: 0,
  },
  [`& .${classes.inputField}`]: {
    padding: '12px 14px',
    fontSize: 14,
  },
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    paddingRight: '8px',
    background: theme.palette.accentSurface.background,
  },
  [`& .${classes.toolbarTitle}`]: {
    flex: '1 0 auto',
    color: theme.palette.accentSurface.text,
    fontSize: '1rem',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontWeight: '400',
    lineHeight: '1.75',
    letterSpacing: '0.00938em'
  },
  [`& .${classes.createDialog}`]: {
    width: '600px'
  },
}));

class NewFlexItemList extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      fields: [{
        id: 0,
        label: 'custom content 1',
        text: '',
        error: false,
        errorLabel: '',
      }],
      lastId: 1,
      reservedKeyWords: ['all'],
      showPlaceholderDialog: true,
      hasPlaceholderPattern: false
    }
  }

  onClickAddMoreButton(event) {
    const offset = this.state.lastId + 1
    const usedText = 'custom content ' + offset
    let newFields = this.state.fields.slice();
    newFields.push({
      id: offset,
      multiline: false,
      label: usedText,
      text: '',
      selected: false,
      error: false,
      errorLabel: ''
    })
    this.setState({fields: newFields, lastId: offset})
  }

  onClickCheckbox(event, fieldId) {
    if(this.isSelected(fieldId)) {
      this.unselectField(fieldId);
    } else {
      this.selectField(fieldId);
    }
  }

  onClickDeleteButton(fieldId) {
    const newFields = this.state.fields.filter(x => x.id !== fieldId)
    this.setState({fields: newFields})
  }

  onChangeText(event, fieldId) {
    const { t } = this.props;
    const index = this.state.fields.findIndex(x => x.id === fieldId);
    const newText = event.target.value.replace(/(\r\n|\n|\r)/gim," ");
    let newFields = [...this.state.fields];

    // Check for placeholder pattern {{...}}
    const placeholderPattern = /\{\{.*?\}\}/g;
    const hasPlaceholders = placeholderPattern.test(newText);

    if(newText != '') {
      newFields[index].selected = true;
    } else {
      newFields[index].selected = false;
    }
    if(this.state.reservedKeyWords.includes(newText)) {
        newFields[index].error = true
        newFields[index].errorLabel = 'reserved keyword'
    } else {
      if(newText != '' && (this.props.data.includes(newText) || this.state.fields.filter(x => x.text == newText).length > 0)) {
        newFields[index].error = true
        newFields[index].errorLabel = t('customContents.errorAlreadyExists')
      }
      else if (newText != '' && /<\/?[a-z][\s\S]*>/i.test(newText)) {
        newFields[index].error = true
        newFields[index].errorLabel = t('customContents.errorNoHtml')
      }
      else {
        newFields[index].error = false
        newFields[index].errorLabel = ''
      }
    }

    newFields[index].text = newText;

    this.setState({
      fields: newFields,
      numSelected: newFields.filter(x => x.selected === true).length,
      hasPlaceholderPattern: hasPlaceholders,
      showPlaceholderDialog: hasPlaceholders || this.state.showPlaceholderDialog
    });
  }

  isSelected(fieldId) {
    return this.state.fields.find(x => x.id == fieldId).selected
  }

  selectField(fieldId) {
    const index = this.state.fields.findIndex(x => x.id === fieldId);
    let newFields = [...this.state.fields];
    newFields[index].selected = true;
    this.setState({fields: newFields, numSelected: this.state.fields.filter(x => x.selected === true).length});
  }

  unselectField(fieldId) {
    const index = this.state.fields.findIndex(x => x.id === fieldId);
    let newFields = [...this.state.fields];
    newFields[index].selected = false;
    this.setState({fields: newFields, numSelected: this.state.fields.filter(x => x.selected === true).length});
  }

  hasSelected() {
    return this.state.numSelected > 0;
  }

  allowDelete() {
    return this.state.fields.length > this.state.numSelected;
  }

  allowSave() {
    const invalidFields = this.state.fields.filter(x => x.selected === true).filter(x => x.text === '' || x.error === true)

    return invalidFields.length === 0
  }

  handlePlaceholderDialogUpdate = () => {
    // Hide the placeholder dialog after settings are updated
    this.setState({ showPlaceholderDialog: false });
  }

  render() {
    const { t } = this.props;

    return (
      <StyledDialog
        fullWidth={true}
        maxWidth={'md'}
        open={true}
        onClose={this.onCloseCreateDialog}
        aria-labelledby="form-dialog-title"
        PaperProps={{
          style: { overflowX: 'hidden' }
        }}
      >
        <DialogTitle style={{paddingBottom: "0", display: 'flex', alignItems: 'center', justifyContent: 'space-between'}} id="form-dialog-title">
          {t('customContents.addCustomContent')}
          <IconButton aria-label="Close" onClick={this.props.onCancel} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>

          <div>
            <CCEnablePlaceholderMatchingDialog
              onUpdateThemeSettings={this.handlePlaceholderDialogUpdate}
              showWhenPlaceholderDetected={this.state.hasPlaceholderPattern}
            />

            <List style={{padding: 0, display: 'flex', flexDirection: 'column', gap: 16}}>
            {this.state.fields.map((field, index) => {
              return (
                <div key={`flexItem-${index}`}>
                  <ListItem disableGutters disabled={this.props.isPending} style={{padding: 0}}>

                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%' }}>

                    <TextField
                      id="outlined-multiline-flexible"
                      multiline
                      minRows="5"
                      maxRows="10"
                      fullWidth
                      autoFocus
                      placeholder={field.label}
                      value={field.text}
                      helperText={field.errorLabel}
                      error={field.error}
                      margin="none"
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        classes: {
                          root: classes.textField,
                          input: classNames(classes.targetField, classes.inputField),
                        }
                      }}
                      className={classes.textField}
                      onChange={event => {this.onChangeText(event, field.id)}}
                      style={{ flex: 1, minWidth: 0 }}
                    />

                    <Tooltip title="Delete" arrow>
                      <div>
                        <IconButton
                          aria-label="Delete"
                          disabled={this.state.fields.length <= 1}
                          onClick={event => {this.onClickDeleteButton(field.id)}}
                          size="large">
                          <DeleteIcon/>
                        </IconButton>
                      </div>
                    </Tooltip>

                    </div>
                  </ListItem>
                </div>
              );
            })}
            </List>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <Tooltip title="Add more" arrow>
                <Fab
                  size="small"
                  color="primary"
                  aria-label="Add more"
                  onClick={event => {this.onClickAddMoreButton(event)}}
                >
                  <AddIcon />
                </Fab>
              </Tooltip>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.props.onCancel} variant="outlined" size="medium">
            {t('system.cancel')}
          </Button>
          <Button disabled={this.hasSelected() && this.allowSave() ? false : true} onClick={e => {this.props.onSave(this.props.scope, this.state.fields)}} variant="contained" type="submit" color="primary" size="medium">
            {t('system.save')}
          </Button>
        </DialogActions>
      </StyledDialog>
    );
  }
}

export default withTranslation()(NewFlexItemList);
